import { useRef, useState, type RefObject, type KeyboardEvent } from 'react';

import type { useProcessedTranscripts } from '../meetings-transcript-display.hooks';
import type { useTranscriptSelection } from './use-transcript-selection';
import type { VirtualizerHandle } from 'virtua';

export function useTranscriptNavigation(
  transcripts: ReturnType<typeof useProcessedTranscripts>,
  selectableKeys: Set<number>,
  selectChunk: ReturnType<typeof useTranscriptSelection>['selectChunk'],
  containerRef: RefObject<HTMLDivElement | null>,
  vlistRef: RefObject<VirtualizerHandle | null>
) {
  const [focusedKey, setFocusedKey] = useState<number>();
  const pendingFocus = useRef<number | undefined>(undefined);
  const selectableChunks = transcripts.filter(({ sourceLineIndex: key }) =>
    selectableKeys.has(key)
  );
  const activeKey =
    focusedKey !== undefined && selectableKeys.has(focusedKey)
      ? focusedKey
      : selectableChunks[0]?.sourceLineIndex;
  const activeIndex = transcripts.findIndex(
    ({ sourceLineIndex: key }) => key === activeKey
  );

  const focusChunk = (key: number | undefined) => {
    if (key === undefined) return;
    setFocusedKey(key);
    pendingFocus.current = key;
    vlistRef.current?.scrollToIndex(
      transcripts.findIndex((chunk) => chunk.sourceLineIndex === key)
    );
    const element = containerRef.current?.querySelector<HTMLElement>(
      `[data-transcript-key="${key}"]`
    );
    if (element) {
      element.focus();
      pendingFocus.current = undefined;
    }
  };
  const handleKeyDown = (key: number, event: KeyboardEvent<HTMLDivElement>) => {
    if (['ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) {
      event.preventDefault();
      const currentIndex = selectableChunks.findIndex(
        (chunk) => chunk.sourceLineIndex === key
      );
      const nextIndex =
        event.key === 'Home'
          ? 0
          : event.key === 'End'
            ? selectableChunks.length - 1
            : Math.max(
                0,
                Math.min(
                  selectableChunks.length - 1,
                  currentIndex + (event.key === 'ArrowDown' ? 1 : -1)
                )
              );
      const nextKey = selectableChunks[nextIndex]?.sourceLineIndex;
      focusChunk(nextKey);
      if (event.shiftKey && nextKey !== undefined) selectChunk(nextKey, event);
      return;
    }
    if (event.key === ' ' || event.key === 'Enter') {
      event.preventDefault();
      selectChunk(key, event);
    }
  };
  return { activeKey, activeIndex, pendingFocus, setFocusedKey, handleKeyDown };
}
